/**
 * Offline PIN check (F4.1-q / F4.1-t). The roster carries one encoded PIN hash per team member;
 * the phone verifies a typed PIN against it with WebCrypto and never stores the PIN itself.
 *
 * Encoding (Django-style, self-describing so the scheme can change without a protocol change):
 *
 *     pbkdf2_sha256$<iterations>$<salt base64>$<hash base64>
 *
 * The backend task (POST /users/me/pin, users.pin_hash) must emit exactly this format, or add a
 * verifier for its scheme with `registerPinVerifier` — feature code only calls `verifyPin`.
 */

export interface PinVerifier {
  /** The `<scheme>` prefix this verifier handles, e.g. "pbkdf2_sha256". */
  readonly scheme: string
  verify(pin: string, encoded: string): Promise<boolean>
}

export const PIN_MIN_LENGTH = 4
export const PIN_MAX_LENGTH = 6

export function isValidPinFormat(pin: string): boolean {
  return new RegExp(`^\\d{${PIN_MIN_LENGTH},${PIN_MAX_LENGTH}}$`).test(pin)
}

function b64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64.replace(/-/g, '+').replace(/_/g, '/'))
  const out = new Uint8Array(new ArrayBuffer(bin.length))
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

function bytesToB64(bytes: Uint8Array): string {
  let s = ''
  bytes.forEach((b) => (s += String.fromCharCode(b)))
  return btoa(s)
}

/** Length-independent comparison so timing does not leak how many bytes matched. */
export function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  let diff = 0
  for (let i = 0; i < a.length; i++) diff |= a[i]! ^ b[i]!
  return diff === 0
}

export interface Pbkdf2Hash {
  scheme: 'pbkdf2_sha256'
  iterations: number
  salt: Uint8Array<ArrayBuffer>
  hash: Uint8Array<ArrayBuffer>
}

export function parsePbkdf2Hash(encoded: string): Pbkdf2Hash | null {
  const parts = encoded.split('$')
  if (parts.length !== 4 || parts[0] !== 'pbkdf2_sha256') return null
  const iterations = Number(parts[1])
  if (!Number.isInteger(iterations) || iterations < 1) return null
  try {
    const salt = b64ToBytes(parts[2]!)
    const hash = b64ToBytes(parts[3]!)
    if (salt.length < 8 || hash.length < 16) return null
    return { scheme: 'pbkdf2_sha256', iterations, salt, hash }
  } catch {
    return null
  }
}

async function derive(
  pin: string,
  salt: Uint8Array<ArrayBuffer>,
  iterations: number,
  bytes: number,
): Promise<Uint8Array> {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(pin),
    'PBKDF2',
    false,
    ['deriveBits'],
  )
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations },
    material,
    bytes * 8,
  )
  return new Uint8Array(bits)
}

export const pbkdf2Sha256Verifier: PinVerifier = {
  scheme: 'pbkdf2_sha256',
  async verify(pin, encoded) {
    const parsed = parsePbkdf2Hash(encoded)
    if (!parsed) return false
    const candidate = await derive(pin, parsed.salt, parsed.iterations, parsed.hash.length)
    return constantTimeEqual(candidate, parsed.hash)
  },
}

/** Make an encoded hash — used by dev fixtures and tests; production hashes come from the server. */
export async function createPbkdf2PinHash(
  pin: string,
  opts: { iterations?: number; salt?: Uint8Array<ArrayBuffer> } = {},
): Promise<string> {
  const iterations = opts.iterations ?? 210_000
  const salt = opts.salt ?? crypto.getRandomValues(new Uint8Array(new ArrayBuffer(16)))
  const hash = await derive(pin, salt, iterations, 32)
  return `pbkdf2_sha256$${iterations}$${bytesToB64(salt)}$${bytesToB64(hash)}`
}

const verifiers = new Map<string, PinVerifier>([
  [pbkdf2Sha256Verifier.scheme, pbkdf2Sha256Verifier],
])

export function registerPinVerifier(v: PinVerifier): void {
  verifiers.set(v.scheme, v)
}

/** Verify against any registered scheme. Unknown schemes and malformed hashes fail closed. */
export async function verifyPin(pin: string, encoded: string | null | undefined): Promise<boolean> {
  if (!encoded || !isValidPinFormat(pin)) return false
  const scheme = encoded.split('$', 1)[0] ?? ''
  const v = verifiers.get(scheme)
  if (!v) return false
  try {
    return await v.verify(pin, encoded)
  } catch {
    return false
  }
}
